import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslation } from 'react-i18next';

/**
 * Small banner at the top of the candidate dashboard that explains the
 * notification feature and lets the candidate grant permission.
 *
 *   - permission === 'granted'     -> gray button (disabled), "enabled" copy
 *   - permission === 'default'     -> blue button, triggers native prompt
 *   - permission === 'denied'      -> blue button, opens an instructions dialog
 *                                     (browsers refuse to re-prompt once denied)
 *   - permission === 'unsupported' -> gray button (disabled), "not supported" copy
 */
const NotificationPermissionBanner = ({ permission, onRequest }) => {
  const { t } = useTranslation();
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const isGranted = permission === 'granted';
  const isUnsupported = permission === 'unsupported';
  const isDenied = permission === 'denied';
  const isDefault = permission === 'default';

  let text;
  let icon;
  if (isGranted) {
    text = t('candidate.notifications.enabled');
    icon = <NotificationsActiveIcon />;
  } else if (isDenied) {
    text = t('candidate.notifications.blocked');
    icon = <NotificationsOffIcon />;
  } else if (isUnsupported) {
    text = t('candidate.notifications.unsupported');
    icon = <NotificationsOffIcon />;
  } else {
    text = t('candidate.notifications.prompt');
    icon = <NotificationsIcon />;
  }

  // "Not granted" (default OR denied) gets the blue call-to-action button,
  // per the original UX spec. For denied we can't re-prompt, so the click
  // opens an instructions dialog instead of firing the request.
  const buttonIsBlue = isDefault || isDenied;
  const buttonDisabled = isGranted || isUnsupported;
  const buttonColor = buttonIsBlue ? 'primary' : 'inherit';
  const buttonLabel = isGranted
    ? t('candidate.notifications.buttonEnabled')
    : t('candidate.notifications.buttonEnable');

  const handleClick = () => {
    if (isDenied) {
      setInstructionsOpen(true);
      return;
    }
    onRequest();
  };

  const instructionsSteps = t('candidate.notifications.blockedInstructionsSteps', {
    returnObjects: true,
    defaultValue: [],
  });
  const stepsArray = Array.isArray(instructionsSteps) ? instructionsSteps : [];

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: isGranted ? '#f5f5f5' : '#e3f2fd',
        }}
      >
        <Box sx={{ color: isGranted ? 'text.secondary' : 'primary.main', display: 'flex' }}>
          {icon}
        </Box>
        <Typography
          variant="body2"
          sx={{ flexGrow: 1, color: isGranted ? 'text.secondary' : 'text.primary' }}
        >
          {text}
        </Typography>
        <Button
          variant="contained"
          color={buttonColor}
          disabled={buttonDisabled}
          onClick={handleClick}
        >
          {buttonLabel}
        </Button>
      </Paper>

      <Dialog
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('candidate.notifications.blockedDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('candidate.notifications.blockedDialogIntro')}
          </DialogContentText>
          {stepsArray.length > 0 && (
            <List dense>
              {stepsArray.map((step, idx) => (
                <ListItem key={idx} sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
                  <ListItemText primary={step} />
                </ListItem>
              ))}
            </List>
          )}
          <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
            {t('candidate.notifications.blockedDialogRefresh')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstructionsOpen(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationPermissionBanner;
